import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, MapPin, User, UserX } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { CounterLoyaltyChip } from '@/components/counter/CounterLoyaltyChip'
import { CounterScanLoyaltyCard } from '@/components/counter/CounterScanLoyaltyCard'
import { toastHelpers } from '@/lib/toast-helpers'
import { PinDialog } from '@/components/dispatch/PinDialog'
import { useRiderAppEnabled } from '@/hooks/useRiderAppEnabled'
import {
  MAX_CUSTOMER_PHONE_LEN,
  customerPhoneShapeError,
} from '@/lib/customerPhone'
import type { CustomerQuickAddress, CustomerQuickLookup, Order } from '@/types'

export function toGuestDateInputValue(iso?: string | null): string {
  if (!iso) return ''
  const s = String(iso).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().slice(0, 10)
}

export type CounterGuestDetailsBodyProps = {
  customerName: string
  setCustomerName: (v: string) => void
  customerEmail: string
  setCustomerEmail: (v: string) => void
  customerPhone: string
  setCustomerPhone: (v: string) => void
  guestBirthday: string
  setGuestBirthday: (v: string) => void
  /** Delivery destination (delivery orders only). */
  deliveryAddress?: string
  setDeliveryAddress?: (v: string) => void
  /** Saved addresses of the picked customer — rendered as tap-to-fill chips
   *  above the delivery textarea. Owned by CounterInterface so the stash
   *  outlives this popover (it unmounts on close). */
  savedAddresses?: CustomerQuickAddress[]
  setSavedAddresses?: (v: CustomerQuickAddress[]) => void
  /** Render the required Delivery address field — true for delivery orders. */
  showDeliveryAddress?: boolean
  existingOrder: Order | null
  onGuestUpdated: (order: Order) => void
  /** Closes the host popover. Called after a suggestion is auto-saved. */
  onClose?: () => void
  /** Show the "type a name or phone to search" helper line above the inputs.
   *  Defaults to true; the recovery modal hides it since it has its own copy. */
  showSearchHint?: boolean
  /** Raised while the loyalty-card viewfinder is live, so a host popover can
   *  stop closing itself on focus-outside (the camera permission bubble steals
   *  focus and would otherwise kill the stream mid-prompt). */
  onCameraActiveChange?: (active: boolean) => void
}

/**
 * Headless body for the Guest CRM section. Renders inside the toolbar popover.
 * Both the Guest name and Phone inputs share a single autocomplete dropdown
 * backed by `/counter/customers/search`; picking a suggestion auto-saves the
 * order and closes the popover (the fast path the standalone rail used to own).
 * Manual entry still goes through the explicit "Save to order" button.
 */
export function CounterGuestDetailsBody({
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  customerPhone,
  setCustomerPhone,
  guestBirthday,
  setGuestBirthday,
  deliveryAddress = '',
  setDeliveryAddress,
  savedAddresses = [],
  setSavedAddresses,
  showDeliveryAddress = false,
  existingOrder,
  onGuestUpdated,
  onClose,
  showSearchHint = true,
  onCameraActiveChange,
}: CounterGuestDetailsBodyProps) {
  const queryClient = useQueryClient()
  const [activeField, setActiveField] = useState<'name' | 'phone' | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const phoneInputRef = useRef<HTMLInputElement | null>(null)

  // Loyalty phone nudge. Customers dedup by phone or email only, so a guest
  // saved with just a name is a fresh profile every visit: their stamp lands
  // on a row nobody can find or redeem, and the receipt drops the "scan to
  // save your visit" QR because a customer is attached. When the programme is
  // on, the first save of a name-only guest focuses Phone instead; the second
  // goes through as typed. Never a hard block — walk-ins must check out.
  const loyaltyEnabledQuery = useQuery({
    queryKey: ['loyalty', 'counter-probe'],
    queryFn: () => apiClient.getPhoneScanEnabled(),
    staleTime: 5 * 60_000,
  })
  const loyaltyEnabled = loyaltyEnabledQuery.data?.data?.loyalty_enabled ?? false
  const needsPhoneNudge =
    loyaltyEnabled && customerName.trim() !== '' && customerPhone.trim() === ''
  const [phoneNudged, setPhoneNudged] = useState(false)
  useEffect(() => {
    if (!needsPhoneNudge) setPhoneNudged(false)
  }, [needsPhoneNudge])

  // True when this save should stop at the phone field instead (first try).
  function nudgeForPhone(): boolean {
    if (!needsPhoneNudge || phoneNudged) return false
    setPhoneNudged(true)
    phoneInputRef.current?.focus()
    return true
  }

  const activeTerm =
    activeField === 'phone' ? customerPhone : activeField === 'name' ? customerName : ''

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(activeTerm.trim()), 150)
    return () => clearTimeout(t)
  }, [activeTerm])

  const searchQuery = useQuery({
    queryKey: ['counter-customer-search', searchTerm],
    queryFn: () => apiClient.searchCustomersQuick(searchTerm),
    enabled: activeField !== null && searchTerm.length >= 1,
    staleTime: 10_000,
  })

  useEffect(() => {
    if (activeField === null) return
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return
      if (!containerRef.current.contains(e.target as Node)) {
        setActiveField(null)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [activeField])

  const matches = useMemo<CustomerQuickLookup[]>(
    () => searchQuery.data?.data?.items ?? [],
    [searchQuery.data],
  )

  // F-DLV-N3: a phone order's drop pin, so the rider's Navigate goes to the
  // door. Only with delivery riders on and once the order exists (the pin is
  // saved on it). The pin isn't on the counter's order payload, so this
  // remembers which order it was set on.
  const riderApp = useRiderAppEnabled()
  const [pinOpen, setPinOpen] = useState(false)
  const [pinnedOrderId, setPinnedOrderId] = useState<string | null>(null)

  const saveMutation = useMutation({
    mutationFn: async (overrides?: Partial<{
      customer_name: string
      customer_email: string
      customer_phone: string
      guest_birthday: string
      delivery_address: string
    }>) => {
      if (!existingOrder?.id) throw new Error('No order')
      const res = await apiClient.updateCounterOrderGuest(existingOrder.id, {
        customer_name: (overrides?.customer_name ?? customerName).trim() || undefined,
        customer_email: (overrides?.customer_email ?? customerEmail).trim() || undefined,
        customer_phone: (overrides?.customer_phone ?? customerPhone).trim() || undefined,
        guest_birthday: (overrides?.guest_birthday ?? guestBirthday).trim() || undefined,
        // Only sent for delivery orders; the field is a pointer server-side, so
        // omitting it leaves the stored address untouched on non-delivery saves.
        // The override matters when selectMatch just prefilled the address —
        // this closure still sees the pre-setState (stale) prop value.
        delivery_address: showDeliveryAddress
          ? (overrides?.delivery_address ?? deliveryAddress).trim()
          : undefined,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not save guest details')
      }
      return res.data
    },
    onSuccess: (order) => {
      onGuestUpdated(order)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
    },
    onError: (e: Error) => {
      toastHelpers.error('Guest details', e.message || 'Save failed')
    },
  })

  function selectMatch(c: CustomerQuickLookup) {
    const phone = c.phone || c.phone_normalized || customerPhone
    if (c.display_name) setCustomerName(c.display_name)
    setCustomerPhone(phone)
    // Stash the customer's saved addresses (default first) in CounterInterface
    // so the tap-to-switch chips work and the default can still fill the field
    // if the cashier flips the order type to delivery after this pick.
    setSavedAddresses?.(c.addresses ?? [])
    // On a delivery order, pre-fill the saved address for this returning
    // customer so the cashier doesn't re-type it. Only when the field is
    // still empty — never clobber an address the cashier has already typed.
    // The textarea stays editable, so tweaks flow back to the profile on save.
    let prefilledAddress: string | undefined
    if (showDeliveryAddress && setDeliveryAddress && c.default_address && !deliveryAddress.trim()) {
      prefilledAddress = c.default_address
      setDeliveryAddress(c.default_address)
    }
    setActiveField(null)
    // No order yet → just pre-fill the four state vars. The values
    // flow through CreateOrder when the cashier picks an order type,
    // so the customer attaches the moment the order is born. Skipping
    // the PATCH here avoids the "No order" toast when the popover is
    // opened on the order-type selection screen.
    if (!existingOrder?.id) {
      toastHelpers.success('Customer info filled', 'Saves when you start the order.')
      onClose?.()
      return
    }
    saveMutation.mutate(
      {
        customer_phone: phone,
        customer_name: c.display_name ?? customerName,
        // Include the just-prefilled address: the mutation closure still sees
        // the stale (empty) deliveryAddress prop, and dropping it here meant
        // the auto-save silently blanked the field server-side.
        ...(prefilledAddress ? { delivery_address: prefilledAddress } : {}),
      },
      {
        onSuccess: () => {
          toastHelpers.success('Customer attached', 'Linked to this order.')
          onClose?.()
        },
      },
    )
  }

  function handleManualSave() {
    if (nudgeForPhone()) return
    if (phoneError) {
      toastHelpers.error('Check the phone number', phoneError)
      return
    }
    saveMutation.mutate(undefined, {
      onSuccess: () => {
        toastHelpers.success('Guest details saved', 'Updated on this order.')
        onClose?.()
      },
    })
  }

  // Enter on any text field commits the typed details. Mirrors the "Save to
  // order" button when an order exists; otherwise the values are already in
  // local state and will flow through CreateOrder, so we just close.
  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    if (e.nativeEvent.isComposing) return
    e.preventDefault()
    if (saveMutation.isPending) return
    if (nudgeForPhone()) return
    if (!existingOrder?.id) {
      toastHelpers.success('Customer info filled', 'Saves when you start the order.')
      onClose?.()
      return
    }
    handleManualSave()
  }

  // Detach the currently attached customer from this order. PATCHes
  // `/counter/orders/:id/guest` with all-empty fields — the handler's
  // resolveCustomerInTx returns nil, and the UPDATE wipes
  // customer_id + customer_phone + customer_name + customer_email +
  // guest_birthday on the order. The customer record itself is left
  // alone (their other orders + loyalty balance are untouched).
  //
  // Critical for testing the "unsaved" receipt state when leftover
  // React state has silently attached a customer the cashier never
  // intended.
  const detachMutation = useMutation({
    mutationFn: async () => {
      if (!existingOrder?.id) throw new Error('No order')
      const res = await apiClient.updateCounterOrderGuest(existingOrder.id, {
        customer_name: undefined,
        customer_email: undefined,
        customer_phone: undefined,
        guest_birthday: undefined,
      })
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Could not remove customer')
      }
      return res.data
    },
    onSuccess: (order) => {
      // Clear local React state too — otherwise the next order pickup
      // will re-attach the same customer via leftover state.
      setCustomerName('')
      setCustomerEmail('')
      setCustomerPhone('')
      setGuestBirthday('')
      setSavedAddresses?.([])
      onGuestUpdated(order)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
      toastHelpers.success('Customer removed', 'No customer attached to this order.')
      onClose?.()
    },
    onError: (e: Error) => {
      toastHelpers.error('Could not remove', e.message || 'Try again')
    },
  })

  // Shown under the Phone input and gates both save paths. The old blur
  // heuristic only asked for ≥4 digits and asked it about *when* to save, not
  // *what* is valid — so "call his brother 0301" auto-persisted and minted a
  // customers row that can never dedup. Mirrors the server check.
  const phoneError = customerPhoneShapeError(customerPhone)

  // Auto-save on Phone blur when the value is a usable phone number and
  // actually changed. Mirrors the behavior the standalone rail used to
  // provide so cashiers can type-and-tab without clicking Save.
  function handlePhoneBlur() {
    setActiveField((f) => (f === 'phone' ? null : f))
    if (!existingOrder?.id) return
    if (saveMutation.isPending) return
    if (phoneError) return
    const digits = customerPhone.replace(/[^0-9]/g, '')
    if (digits.length < 4) return
    if ((existingOrder.customer_phone ?? '').trim() === customerPhone.trim()) return
    saveMutation.mutate(undefined)
  }

  const dropdownVisible =
    activeField !== null && searchTerm.length >= 1 && !saveMutation.isPending

  return (
    <div ref={containerRef} className="space-y-3">
      {existingOrder?.customer_id && (
        <>
          <CounterLoyaltyChip
            orderId={existingOrder.id}
            customerId={existingOrder.customer_id}
          />
          <div className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
            <span className="font-medium">Customer attached</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-2 text-[11px] text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:text-amber-200 dark:hover:bg-amber-900/40"
              disabled={detachMutation.isPending}
              onClick={() => detachMutation.mutate()}
            >
              {detachMutation.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <UserX className="h-3 w-3" />
              )}
              Remove
            </Button>
          </div>
        </>
      )}
      {/* Scanning is available on a brand-new cart too, not only once an order
          row exists. Resolving a card needs no order — only the attach does —
          and gating the whole control on existingOrder meant neither the USB
          scanner nor the camera ever appeared on an ordinary walk-in sale. */}
      {!existingOrder?.customer_id && (
        <CounterScanLoyaltyCard
          orderId={existingOrder?.id ?? null}
          onCameraActiveChange={onCameraActiveChange}
          onAttached={(order) => {
            if (order.customer_name) setCustomerName(order.customer_name)
            if (order.customer_phone) setCustomerPhone(order.customer_phone)
            onGuestUpdated(order)
            queryClient.invalidateQueries({ queryKey: ['orders'] })
            queryClient.invalidateQueries({ queryKey: ['counterPendingOrders'] })
          }}
          onResolved={({ name, phone }) => {
            // No order yet: stash the guest locally. Order creation picks these
            // up, so the customer is attached the moment the order exists.
            if (name) setCustomerName(name)
            if (phone) setCustomerPhone(phone)
          }}
        />
      )}
      {showSearchHint && (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Type a name or phone to search existing customers. Picking one attaches it to the order; otherwise click Save to keep the typed details.
        </p>
      )}

      <div className="relative space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Guest name</Label>
        <Input
          value={customerName}
          onChange={(e) => setCustomerName(e.target.value)}
          onFocus={() => setActiveField('name')}
          onBlur={() => setActiveField((f) => (f === 'name' ? null : f))}
          onKeyDown={handleInputKeyDown}
          className="h-9 text-sm placeholder:text-xs placeholder:text-muted-foreground/60"
          placeholder="Shown on ticket and receipt"
          autoComplete="off"
          spellCheck={false}
        />
        {activeField === 'name' && (
          <AutocompleteDropdown
            visible={dropdownVisible}
            loading={searchQuery.isLoading}
            matches={matches}
            term={searchTerm}
            onPick={selectMatch}
          />
        )}
      </div>

      <div className="relative space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Phone</Label>
        <Input
          ref={phoneInputRef}
          className="h-9 text-sm placeholder:text-xs placeholder:text-muted-foreground/60"
          inputMode="tel"
          value={customerPhone}
          onChange={(e) => setCustomerPhone(e.target.value)}
          onFocus={() => setActiveField('phone')}
          onBlur={handlePhoneBlur}
          onKeyDown={handleInputKeyDown}
          placeholder="Phone number"
          autoComplete="off"
          spellCheck={false}
          maxLength={MAX_CUSTOMER_PHONE_LEN}
          aria-invalid={phoneError ? true : undefined}
        />
        {phoneError && (
          <p className="text-[11px] leading-snug text-destructive">{phoneError}</p>
        )}
        {needsPhoneNudge && (
          <p className="text-[11px] leading-snug text-amber-700 dark:text-amber-300">
            {phoneNudged
              ? 'No phone? Save again to continue — this visit won\'t earn a loyalty stamp they can use.'
              : 'Add their phone so this visit counts toward loyalty. A name alone can\'t be found next time.'}
          </p>
        )}
        {activeField === 'phone' && (
          <AutocompleteDropdown
            visible={dropdownVisible}
            loading={searchQuery.isLoading}
            matches={matches}
            term={searchTerm}
            onPick={selectMatch}
          />
        )}
      </div>
      {showDeliveryAddress && (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-foreground">
            Delivery address <span className="text-destructive">*</span>
          </Label>
          {savedAddresses.length > 0 && setDeliveryAddress && (
            <div className="flex flex-wrap gap-1.5">
              {savedAddresses.map((a) => {
                const active = deliveryAddress.trim() === a.address.trim()
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setDeliveryAddress(a.address)}
                    className={`max-w-full truncate rounded-md border px-2 py-1 text-left text-[11px] transition-colors ${
                      active
                        ? 'border-primary/60 bg-primary/10 text-foreground'
                        : 'border-border bg-muted/40 text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    }`}
                    title={a.address}
                  >
                    <span className="font-medium">
                      {a.label || (a.is_default ? 'Default' : 'Saved')}
                    </span>
                    {a.is_default && a.label ? ' · default' : ''}
                    {' — '}
                    {a.address}
                  </button>
                )
              })}
            </div>
          )}
          <textarea
            value={deliveryAddress}
            onChange={(e) => setDeliveryAddress?.(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-xs placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="House / flat, street, area, landmark"
            spellCheck={false}
          />
          <p className="text-[11px] leading-snug text-muted-foreground">
            Required for delivery. Printed on the receipt for the driver. Saved to
            the customer for next time — edit here to update it.
          </p>
          {riderApp && existingOrder?.id && (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setPinOpen(true)}>
                <MapPin className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {pinnedOrderId === existingOrder.id ? 'Pin set · change' : 'Add map pin'}
              </Button>
              <PinDialog
                open={pinOpen}
                orderId={existingOrder.id}
                orderNumber={existingOrder.order_number ?? ''}
                address={deliveryAddress.trim() || null}
                initial={null}
                onSaved={(p) => setPinnedOrderId(p ? existingOrder.id : null)}
                onClose={() => setPinOpen(false)}
              />
            </>
          )}
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Email</Label>
        <Input
          type="email"
          className="h-9 text-sm placeholder:text-xs placeholder:text-muted-foreground/60"
          value={customerEmail}
          onChange={(e) => setCustomerEmail(e.target.value)}
          onKeyDown={handleInputKeyDown}
          placeholder="email@example.com"
          autoComplete="email"
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-foreground">Birthday</Label>
        <DatePicker
          value={guestBirthday}
          onChange={setGuestBirthday}
          placeholder="Optional"
          className="h-9 text-sm data-[empty=true]:text-xs data-[empty=true]:text-muted-foreground/60"
        />
      </div>

      {existingOrder && (
        <Button
          type="button"
          size="sm"
          className="w-full"
          disabled={saveMutation.isPending}
          onClick={handleManualSave}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save to order'}
        </Button>
      )}
    </div>
  )
}

function AutocompleteDropdown({
  visible,
  loading,
  matches,
  term: _term,
  onPick,
}: {
  visible: boolean
  loading: boolean
  matches: CustomerQuickLookup[]
  term: string
  onPick: (c: CustomerQuickLookup) => void
}) {
  if (!visible) return null
  return (
    <div className="absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-xl ring-1 ring-black/[0.06] dark:ring-white/[0.08]">
      {loading ? (
        <div className="flex items-center gap-2 px-3 py-2.5 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Searching…
        </div>
      ) : matches.length === 0 ? (
        <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground">
          <span>New customer — press</span>
          <kbd className="inline-flex h-4 items-center rounded border border-border bg-background px-1 font-mono text-[10px] text-foreground">
            Enter
          </kbd>
          <span>to save</span>
        </div>
      ) : (
        <ul className="py-1 text-sm">
          {matches.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                // Critical: onMouseDown + preventDefault so the action
                // runs before the input loses focus and our blur handler
                // races to auto-save the typed-but-unpicked value.
                onMouseDown={(e) => {
                  e.preventDefault()
                  onPick(c)
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent/60 focus:bg-accent/60 focus:outline-none"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <User className="h-3.5 w-3.5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-foreground">
                    {c.display_name || (
                      <span className="italic text-muted-foreground">No name</span>
                    )}
                  </span>
                  <span className="block truncate font-mono text-xs text-muted-foreground">
                    {c.phone_normalized || c.phone || '—'}
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                  {c.visit_count} {c.visit_count === 1 ? 'visit' : 'visits'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Returns true when any guest field has content — used by the toolbar to
 * decorate the icon trigger with an indicator dot.
 */
export function hasGuestDetailsDraft(values: {
  customerName: string
  customerEmail: string
  customerPhone: string
  guestBirthday: string
}): boolean {
  return Boolean(
    values.customerName.trim() ||
      values.customerEmail.trim() ||
      values.customerPhone.trim() ||
      values.guestBirthday.trim()
  )
}
